/**
 * SalesReportPage — Issue #373.
 *
 * 销售管理报表：标准口径测算读模型。
 * 三段结构：渠道开发与拓展 / 业绩预期与达成 / 订单数据与指标。
 *
 * 约束：
 *   - 所有数据通过 @jinzhuan/api-client；禁止前端假状态。
 *   - 成本 / 毛利 / 提成列仅在用户具备 finance.sales_report:reveal 权限时展示。
 *   - 未接入数据源展示受控空状态标签，不展示 0 或假数值。
 *   - 导出操作写入审计（后端）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  canSeeSalesReport,
  canGenerateSalesReport,
  canReviewSalesReport,
  canPublishSalesReport,
  canExportSalesReport,
  canRevealSalesReportDetail,
} from '@/lib/financePermissions';
import { formatRatio } from '@/lib/decimalUtils';
import { getStoredAccessToken } from '@/lib/tokenStorage';
import {
  useSalesReportBatches,
  useSalesReportBatchDetail,
  useSalesReportActions,
  coerceBatchStatus,
  type SalesReportBatchSummaryDto,
  type SalesReportBatchDetailDto,
} from '@/hooks/useFinanceSalesReport';

// ─── Status labels ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  generated: '已生成',
  finance_reviewed: '财务已复核',
  published: '已发布',
  archived: '已归档',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  generated: 'bg-sky-100 text-sky-700',
  finance_reviewed: 'bg-indigo-100 text-indigo-700',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-200 text-gray-500',
};

const SOURCE_STATUS_LABELS: Record<string, string> = {
  connected: '已连接',
  partial: '部分接入',
  pending_source: '待数据源接入',
  not_connected: '未接入',
};

const SOURCE_STATUS_COLORS: Record<string, string> = {
  connected: 'text-green-700',
  partial: 'text-amber-600',
  pending_source: 'text-amber-600',
  not_connected: 'text-gray-400',
};

const BATCH_SOURCE_STATUS_LABELS: Record<string, string> = {
  all_connected: '全部数据源已连接',
  partial: '部分数据源已连接',
  pending: '待数据源接入',
};

// ─── Authenticated export ─────────────────────────────────────────────────────

/**
 * Download the sales report CSV through an authenticated fetch.
 *
 * The previous implementation used a raw `<a href>` anchor which does not
 * attach the localStorage-stored Bearer token — `JwtAuthGuard` would reject
 * the request with 401 and the browser would render the JSON error page as
 * the downloaded "file". This helper:
 *   1. Pulls the access token from `tokenStorage` (the same place
 *      `createApiClient` reads it from) and sends it as `Authorization: Bearer`.
 *   2. Asserts a 2xx response and a `text/csv` content type so an HTML error
 *      page or a forgotten reveal-permission response cannot be saved as a
 *      "csv" file with misleading contents.
 *   3. Streams the body into a Blob and triggers a synthetic download via
 *      `URL.createObjectURL`, so the browser still surfaces a save dialog and
 *      we do not need to commit a server-side cookie-auth path.
 *
 * On any failure the caller receives an `Error` with a Chinese message ready
 * to surface in the page banner.
 */
type SalesReportExportFormat = 'csv' | 'xlsx';

const SALES_REPORT_EXPORT_MIME: Record<SalesReportExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

async function downloadSalesReport(args: {
  batchId: string;
  reportPeriod: string;
  format: SalesReportExportFormat;
}): Promise<void> {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error('未登录或会话已失效，请重新登录后再导出');
  }
  const expectedMime = SALES_REPORT_EXPORT_MIME[args.format];
  const url = `/api/v1/finance/sales-reports/${encodeURIComponent(args.batchId)}/export?format=${args.format}&reason=${encodeURIComponent('手动导出')}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: expectedMime,
      },
      credentials: 'include',
    });
  } catch (err) {
    throw new Error(
      `网络错误，导出失败：${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('会话已失效，请重新登录后再导出');
    }
    if (response.status === 403) {
      throw new Error('当前账号没有导出销售管理报表的权限');
    }
    if (response.status === 404) {
      throw new Error('报表不存在或已删除');
    }
    throw new Error(`导出失败（HTTP ${response.status}）`);
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes(expectedMime)) {
    throw new Error(
      `导出格式异常：服务器返回 ${contentType || '未知'}，预期 ${expectedMime}`,
    );
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const filename = `sales_report_${args.reportPeriod}.${args.format}`;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

// ─── Components ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function SourceStatusTag({ status }: { status: string }) {
  return (
    <span className={`text-xs font-medium ${SOURCE_STATUS_COLORS[status] ?? 'text-gray-400'}`}>
      [{SOURCE_STATUS_LABELS[status] ?? status}]
    </span>
  );
}

function RestrictedCell() {
  return <span className="text-xs text-gray-400 italic">[无权限]</span>;
}

function PendingSourceCell({ label }: { label?: string }) {
  return (
    <span className="text-xs text-amber-600 italic">
      {label ?? '待数据源接入'}
    </span>
  );
}

function NotConnectedCell({ label }: { label?: string }) {
  return (
    <span className="text-xs text-gray-400 italic">
      {label ?? '未接入'}
    </span>
  );
}

function DecimalCell({
  value,
  status,
  canReveal,
}: {
  value: string;
  status?: string;
  canReveal: boolean;
}) {
  if (!canReveal) return <RestrictedCell />;
  if (status === 'not_connected') return <NotConnectedCell />;
  if (status === 'pending_source') return <PendingSourceCell />;
  return <span className="tabular-nums">{value}</span>;
}

// ─── Section 1: Channel development ───────────────────────────────────────────

function ChannelSection({
  rows,
}: {
  rows: NonNullable<SalesReportBatchDetailDto['channelRows']>;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-500">
        本期无渠道开发数据
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['序号', '开发人员', '跟进渠道数（累计）', '拓展成本（累计）', '本期新增', '累计', '备注', '数据状态'].map(
              (h) => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2">{r.seqNo}</td>
              <td className="px-3 py-2">{r.developerName ?? r.developerId ?? '-'}</td>
              <td className="px-3 py-2 tabular-nums">{r.followChannelCountTotal}</td>
              <td className="px-3 py-2">
                {r.sourceStatus === 'connected' ? (
                  <span className="tabular-nums">{r.developmentCostTotal}</span>
                ) : (
                  <SourceStatusTag status={r.sourceStatus} />
                )}
              </td>
              <td className="px-3 py-2 tabular-nums">{r.newChannelCount}</td>
              <td className="px-3 py-2 tabular-nums">{r.channelCountTotal}</td>
              <td className="px-3 py-2 text-gray-500">{r.notes ?? '-'}</td>
              <td className="px-3 py-2">
                <SourceStatusTag status={r.sourceStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Section 2: Performance ────────────────────────────────────────────────────

function PerformanceSection({
  rows,
}: {
  rows: NonNullable<SalesReportBatchDetailDto['performanceRows']>;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-500">
        本期无顾问业绩数据
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['序号', '健管顾问', '目标业绩', '实际业绩', '回款金额', '回款率', '备注', '数据状态'].map(
              (h) => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => {
            // Audit round-4 #2: target performance with a non-connected source
            // must surface as a controlled state, never as 0.000000. The DTO
            // exposes targetAmountStatus alongside the archive value.
            const targetStatus = r.targetAmountStatus ?? 'pending_source';
            return (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">{r.seqNo}</td>
                <td className="px-3 py-2">{r.consultantName ?? r.consultantId ?? '-'}</td>
                <td className="px-3 py-2">
                  {targetStatus === 'connected' ? (
                    <span className="tabular-nums">{r.targetAmount}</span>
                  ) : (
                    <SourceStatusTag status={targetStatus} />
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{r.actualPerformance}</td>
                <td className="px-3 py-2 tabular-nums">{r.receivedAmount}</td>
                <td className="px-3 py-2 tabular-nums">{formatRatio(r.collectionRate)}</td>
                <td className="px-3 py-2 text-gray-500">{r.notes ?? '-'}</td>
                <td className="px-3 py-2">
                  <SourceStatusTag status={r.sourceStatus} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Section 3: Order indicators ──────────────────────────────────────────────

// Render the actual-ledger cell for a given indicator key. Audit round-4 #3:
// the 实际收支列示 column must show confirmed actual values when present, or
// a controlled status label when not connected — never a fabricated number.
function ActualLedgerCell({
  entry,
  canReveal,
}: {
  entry:
    | NonNullable<
        NonNullable<SalesReportBatchDetailDto['indicatorRow']>['actualLedgerListing']
      >[number]
    | undefined;
  canReveal: boolean;
}) {
  if (!entry) {
    return <SourceStatusTag status="not_connected" />;
  }
  if (entry.value == null) {
    return <SourceStatusTag status={entry.status} />;
  }
  if (!canReveal) {
    return <RestrictedCell />;
  }
  return <span className="tabular-nums">{entry.value}</span>;
}

function IndicatorSection({
  row,
  canReveal,
}: {
  row: NonNullable<SalesReportBatchDetailDto['indicatorRow']> | undefined;
  canReveal: boolean;
}) {
  if (!row) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-500">
        指标数据尚未生成，请点击"生成报表"。
      </div>
    );
  }

  // The derived statuses come from the backend (controller mapping), but if
  // an older response shape is in cache, fall back so the rendering still
  // makes a conservative pending decision rather than showing fake numbers.
  const grossProfitStatus = row.grossProfitStatus ?? 'pending_source';
  const grossMarginStatus = row.grossMarginStatus ?? grossProfitStatus;

  const ledgerByKey = new Map<
    string,
    NonNullable<typeof row.actualLedgerListing>[number]
  >();
  for (const entry of row.actualLedgerListing ?? []) {
    ledgerByKey.set(entry.indicatorKey, entry);
  }

  const fields: Array<{
    indicatorKey: string;
    label: string;
    value: string;
    status?: string;
    sensitive?: boolean;
  }> = [
    { indicatorKey: 'order_amount_total', label: '订单总金额', value: row.orderAmountTotal },
    { indicatorKey: 'channel_order_amount', label: '渠道客户订单', value: row.channelOrderAmount },
    {
      indicatorKey: 'direct_order_amount',
      label: '自营客户订单（含转介绍）',
      value: row.directOrderAmount,
    },
    {
      indicatorKey: 'channel_share',
      label: '渠道分成',
      value: row.channelShare,
      status: row.channelShareStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'staff_commission',
      label: '人员提成',
      value: row.staffCommission,
      status: row.staffCommissionStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'purchase_cost',
      label: '采购成本',
      value: row.purchaseCost,
      status: row.purchaseCostStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'preparation_cost',
      label: '制备成本（自原代接收到出库）',
      value: row.preparationCost,
      status: row.preparationCostStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'external_service_cost',
      label: '外部服务费用',
      value: row.externalServiceCost,
      status: row.externalServiceStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'gross_profit',
      label: '订单毛利',
      value: row.grossProfit,
      // Rolled-up status: DecimalCell renders [待数据源接入] / [未接入] when
      // any cost component is missing, instead of a fake numeric result.
      status: grossProfitStatus,
      sensitive: true,
    },
    {
      indicatorKey: 'gross_margin',
      label: '毛利率',
      // grossMargin is masked to '[restricted]' for non-reveal users; only
      // format as a percentage when the Decimal string is actually present so
      // the `[restricted]` literal flows through unchanged via DecimalCell.
      value: canReveal ? formatRatio(row.grossMargin) : row.grossMargin,
      status: grossMarginStatus,
      sensitive: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">指标项</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                标准口径测算值
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                实际收支列示
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">数据源状态</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {fields.map((f) => (
              <tr key={f.indicatorKey} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-700">{f.label}</td>
                <td className="px-3 py-2">
                  {f.sensitive ? (
                    <DecimalCell
                      value={f.value}
                      status={f.status}
                      canReveal={canReveal}
                    />
                  ) : (
                    <span className="tabular-nums">{f.value}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <ActualLedgerCell
                    entry={ledgerByKey.get(f.indicatorKey)}
                    canReveal={canReveal}
                  />
                </td>
                <td className="px-3 py-2">
                  {f.status ? <SourceStatusTag status={f.status} /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actual ledger sources — kept as a supplementary status list to make
          the per-source reason visible. The per-row 实际收支列示 column above
          is the canonical template column; this listing surfaces the reason
          string and any extra source-types not present in the per-row map. */}
      {row.actualLedgerSources && row.actualLedgerSources.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            实际收支来源接入状态
          </h4>
          <ul className="space-y-1">
            {row.actualLedgerSources.map((s) => (
              <li key={s.sourceType} className="flex items-center gap-2 text-sm">
                <SourceStatusTag status={s.status} />
                <span className="text-gray-700">{s.displayLabel}</span>
                {s.reason && (
                  <span className="text-xs text-gray-400">— {s.reason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Standard calculation snapshot */}
      {row.standardCalculation && (
        <details className="rounded-md border border-gray-200 p-3">
          <summary className="cursor-pointer text-xs font-medium text-gray-500">
            标准口径测算快照（规则集 {row.standardCalculation.rulesetVersion}）
          </summary>
          <pre className="mt-2 overflow-auto text-xs text-gray-600">
            {JSON.stringify(row.standardCalculation, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─── Generate form ─────────────────────────────────────────────────────────────

function GenerateForm({
  onGenerate,
  loading,
}: {
  onGenerate: (period: string, notes?: string) => void;
  loading: boolean;
}) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [notes, setNotes] = useState('');

  const period = `${year}-${String(month).padStart(2, '0')}`;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-dashed border-gray-300 p-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">年份</label>
        <input
          type="number"
          min={2020}
          max={2100}
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">月份</label>
        <select
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value, 10))}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m} 月
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1">
        <label className="mb-1 block text-xs font-medium text-gray-600">备注（可选）</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="本次生成说明"
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
      </div>
      <button
        onClick={() => onGenerate(period, notes || undefined)}
        disabled={loading}
        className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {loading ? '生成中...' : `生成 ${period} 报表`}
      </button>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function SalesReportPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];

  const canRead = canSeeSalesReport(granted);
  const canGenerate = canGenerateSalesReport(granted);
  const canReview = canReviewSalesReport(granted);
  const canPublish = canPublishSalesReport(granted);
  const canExport = canExportSalesReport(granted);
  const canReveal = canRevealSalesReportDetail(granted);

  const { data: listData, loading: listLoading, error: listError, fetchList } = useSalesReportBatches();
  const { data: detail, loading: detailLoading, error: detailError, fetchDetail } = useSalesReportBatchDetail();
  const actions = useSalesReportActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const handleExport = async (
    b: SalesReportBatchSummaryDto,
    format: SalesReportExportFormat,
  ) => {
    setActionMessage(null);
    setExportingId(`${b.id}:${format}`);
    try {
      await downloadSalesReport({
        batchId: b.id,
        reportPeriod: b.reportPeriod,
        format,
      });
      setActionMessage(`报表 ${b.reportPeriod} 已导出（${format.toUpperCase()}）`);
    } catch (err) {
      setActionMessage(
        `导出失败：${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setExportingId(null);
    }
  };

  useEffect(() => {
    if (canRead) {
      void fetchList({ status: coerceBatchStatus(statusFilter) });
    }
  }, [fetchList, statusFilter, canRead]);

  useEffect(() => {
    if (selectedId) {
      void fetchDetail(selectedId);
    }
  }, [fetchDetail, selectedId]);

  const batches = useMemo(() => listData?.data ?? [], [listData]);

  const handleGenerate = async (period: string, notes?: string) => {
    setActionMessage(null);
    const result = await actions.generate({ reportPeriod: period, notes });
    if (result) {
      setActionMessage(`报表 ${result.reportPeriod} 已生成（状态：${STATUS_LABELS[result.status] ?? result.status}）`);
      void fetchList({ status: coerceBatchStatus(statusFilter) });
      setShowGenerate(false);
    } else if (actions.error) {
      setActionMessage(`生成失败：${actions.error.message}`);
    }
  };

  const handleReview = async (id: string, version: number) => {
    setActionMessage(null);
    const result = await actions.review(id, { expectedVersion: version });
    if (result) {
      setActionMessage('财务复核完成');
      if (selectedId === id) void fetchDetail(id);
    } else if (actions.error) {
      setActionMessage(`复核失败：${actions.error.message}`);
    }
  };

  const handlePublish = async (id: string, version: number) => {
    setActionMessage(null);
    const result = await actions.publish(id, { expectedVersion: version });
    if (result) {
      setActionMessage('报表已发布');
      if (selectedId === id) void fetchDetail(id);
    } else if (actions.error) {
      setActionMessage(`发布失败：${actions.error.message}`);
    }
  };

  if (!canRead) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        您没有查看销售管理报表的权限（需要 finance.sales_report:read）。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">销售管理报表</h1>
        <div className="flex items-center gap-2">
          {canGenerate && (
            <button
              onClick={() => setShowGenerate((v) => !v)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50"
            >
              {showGenerate ? '收起' : '+ 生成报表'}
            </button>
          )}
        </div>
      </div>

      {actionMessage && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {actionMessage}
        </div>
      )}

      {showGenerate && canGenerate && (
        <GenerateForm onGenerate={handleGenerate} loading={actions.loading} />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">状态筛选：</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {/* Batch list */}
      {listLoading && <div className="text-sm text-gray-500">加载中...</div>}
      {listError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          加载失败：{listError.message}
        </div>
      )}
      {!listLoading && batches.length === 0 && (
        <div className="rounded-md border border-gray-100 p-6 text-center text-sm text-gray-500">
          {statusFilter ? '没有符合条件的报表' : '尚未生成任何报表，点击"生成报表"开始'}
        </div>
      )}
      {batches.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['报告期', '规则集版本', '状态', '数据源状态', '生成时间', '操作'].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batches.map((b: SalesReportBatchSummaryDto) => (
                <tr
                  key={b.id}
                  className={`cursor-pointer hover:bg-gray-50 ${selectedId === b.id ? 'bg-sky-50' : ''}`}
                  onClick={() => setSelectedId(selectedId === b.id ? null : b.id)}
                >
                  <td className="px-3 py-2 font-medium">{b.reportPeriod}</td>
                  <td className="px-3 py-2 text-gray-500">{b.standardRulesetVersion}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {BATCH_SOURCE_STATUS_LABELS[b.sourceStatus] ?? b.sourceStatus}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {b.generatedAt
                      ? new Date(b.generatedAt).toLocaleString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {canExport && (
                        <>
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                            disabled={exportingId === `${b.id}:csv`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleExport(b, 'csv');
                            }}
                          >
                            {exportingId === `${b.id}:csv`
                              ? '导出中...'
                              : '导出 CSV'}
                          </button>
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                            disabled={exportingId === `${b.id}:xlsx`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleExport(b, 'xlsx');
                            }}
                          >
                            {exportingId === `${b.id}:xlsx`
                              ? '导出中...'
                              : '导出 Excel'}
                          </button>
                        </>
                      )}
                      {canReview && b.status === 'generated' && (
                        <button
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleReview(b.id, b.recordVersion);
                          }}
                        >
                          财务复核
                        </button>
                      )}
                      {canPublish && b.status === 'finance_reviewed' && (
                        <button
                          className="text-xs text-green-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePublish(b.id, b.recordVersion);
                          }}
                        >
                          发布
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selectedId && (
        <div className="space-y-6 rounded-md border border-gray-200 p-4">
          {detailLoading && <div className="text-sm text-gray-500">加载详情...</div>}
          {detailError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              加载详情失败：{detailError.message}
            </div>
          )}
          {detail && detail.id === selectedId && (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">
                  {detail.reportPeriod} 报表详情
                </h2>
                <StatusBadge status={detail.status} />
              </div>

              {!canReveal && (
                <div className="rounded-md border border-amber-100 bg-amber-50 p-2 text-xs text-amber-700">
                  部分列（成本、毛利、提成）需要 reveal 权限方可查看。
                </div>
              )}

              {/* Section 1 */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  第一段：渠道开发与拓展
                </h3>
                <ChannelSection rows={detail.channelRows ?? []} />
              </section>

              {/* Section 2 */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  第二段：业绩预期与达成
                </h3>
                <PerformanceSection rows={detail.performanceRows ?? []} />
              </section>

              {/* Section 3 */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  第三段：订单数据与指标
                </h3>
                <IndicatorSection row={detail.indicatorRow} canReveal={canReveal} />
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
}
